<template>
  <div class="base-phone-list">
    <div v-if="selecting" class="base-phone-list__selection-bar">
      <FormCheckbox
        :value="allSelected"
        :indeterminate="someSelected"
        :afterIndeterminate="false"
        :onChange="toggleAll"
        :aria-label="$t('table.phone-list.select-all')"
      />
      <span class="base-phone-list__selection-count">
        {{ $tc('table.phone-list.selected', selectedItems.length, { count: selectedItems.length }) }}
      </span>
      <slot v-if="selectedItems.length" name="selection-actions" :items="selectedItems" :clear="clearSelection" />
    </div>

    <ul class="base-phone-list__items">
      <template v-for="item in items">
        <slot name="item" :item="item" :selected="selectedSet.has(item[itemKey])" :toggle="toggle" />
      </template>
    </ul>

    <div ref="sentinel" class="base-phone-list__footer" :class="{ 'is-tight': !roomBelow }" aria-live="polite">
      <v-progress-circular
        v-if="loadingMore"
        indeterminate
        size="24"
        width="2"
        color="primary"
        :aria-label="$t('table.phone-list.loading')"
      />
      <template v-else-if="loadMoreError || (hasMore && !canObserve)">
        <p v-if="loadMoreError" class="base-phone-list__footer-text">
          {{ $t('table.phone-list.load-more-failed') }}
        </p>
        <button type="button" class="base-phone-list__more" @click="$emit('load-more')">
          {{ loadMoreError ? $t('table.phone-list.retry') : $t('table.phone-list.load-more') }}
        </button>
      </template>
      <p v-else-if="saysEnd" class="base-phone-list__footer-text">{{ $t('table.phone-list.end') }}</p>
    </div>

    <BaseScrollTop />

    <BaseBottomSheet
      :value="sortOpen"
      :title="$t('table.phone-list.sort-title')"
      @input="$emit('update:sortOpen', $event)"
    >
      <ul class="base-bottom-sheet__menu" role="radiogroup">
        <li v-for="option in sortOptions" :key="option.key">
          <button
            type="button"
            role="radio"
            :aria-checked="String(option.key === sortKey)"
            :class="{ 'is-active': option.key === sortKey }"
            @click="sort(option)"
          >
            {{ option.label }}
            <BaseIcon v-if="option.key === sortKey" name="check" class="base-bottom-sheet__menu-check" />
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseScrollTop from '@/components/base/BaseScrollTop.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import { PHONE_PAGE_SIZE } from '@/util/phone-table';

// The phone version of a table: compact rows (the `item` slot), the next page loaded as the
// reader nears the end, a selection mode for the bulk actions (the `selection-actions` slot),
// the sort choices in a sheet, and a way back to the top of a long list.
export default {
  name: 'BasePhoneList',
  components: {
    BaseBottomSheet,
    BaseIcon,
    BaseScrollTop,
    FormCheckbox,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    itemKey: {
      type: String,
      default: 'id',
    },
    selecting: {
      type: Boolean,
      default: false,
    },
    sortOpen: {
      type: Boolean,
      default: false,
    },
    // `{ key, label }` each, and the key of the order shown.
    sortOptions: {
      type: Array,
      default: () => [],
    },
    sortKey: {
      type: String,
      default: '',
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    // Whether to say so once every item is listed (not when more exist that the reader can't load).
    showEnd: {
      type: Boolean,
      default: true,
    },
    // Space under the list for the page's floating buttons. A list inside a page, with more
    // under it, leaves none when it has nothing to say.
    roomBelow: {
      type: Boolean,
      default: true,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    // A new search is running: asking for more now would cancel it.
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedIds: [],
      observer: null,
      canObserve: typeof IntersectionObserver !== 'undefined',
    };
  },
  computed: {
    // "Fin des résultats", only under a list longer than a page, which the reader scrolled through
    // as it grew: a shorter one plainly ends where it does.
    saysEnd() {
      return !this.hasMore && this.showEnd && this.items.length > PHONE_PAGE_SIZE;
    },
    // One lookup per row, however many items the list has grown to.
    selectedSet() {
      return new Set(this.selectedIds);
    },
    selectedItems() {
      return this.items.filter((item) => this.selectedSet.has(item[this.itemKey]));
    },
    allSelected() {
      return this.items.length > 0 && this.selectedItems.length === this.items.length;
    },
    someSelected() {
      return this.selectedItems.length > 0 && !this.allSelected;
    },
  },
  watch: {
    selecting(selecting) {
      if (!selecting) this.clearSelection();
    },
    items(items, previous) {
      // A new search replaces the list: drop the selection. A loaded page keeps it.
      const key = this.itemKey;
      const replaced = !previous.length || !items.length || items[0][key] !== previous[0][key];
      if (replaced) this.clearSelection();
      this.$nextTick(this.observeSentinel);
    },
    pending(pending) {
      if (!pending) this.$nextTick(this.observeSentinel);
    },
    loadingMore(loadingMore) {
      if (!loadingMore) this.$nextTick(this.observeSentinel);
    },
  },
  methods: {
    toggle(id) {
      this.selectedIds = this.selectedIds.includes(id)
        ? this.selectedIds.filter((selected) => selected !== id)
        : [...this.selectedIds, id];
    },
    toggleAll() {
      this.selectedIds = this.allSelected || this.someSelected ? [] : this.items.map((item) => item[this.itemKey]);
    },
    clearSelection() {
      this.selectedIds = [];
    },
    sort(option) {
      this.$emit('update:sortOpen', false);
      if (option.key !== this.sortKey) this.$emit('sort', option);
    },
    // Observing again makes the observer report the footer's current visibility, which it
    // otherwise only does when that changes: after a page that leaves the footer in view,
    // the next one must still load.
    observeSentinel() {
      if (!this.observer || !this.$refs.sentinel) return;
      this.observer.unobserve(this.$refs.sentinel);
      this.observer.observe(this.$refs.sentinel);
    },
    onSentinel(entries) {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible && this.hasMore && !this.loadingMore && !this.loadMoreError && !this.pending) {
        this.$emit('load-more');
      }
    },
  },
  mounted() {
    if (!this.canObserve) return;
    // Starts loading a screen or so before the end, so the reader rarely waits.
    this.observer = new IntersectionObserver(this.onSentinel, { rootMargin: '0px 0px 600px 0px' });
    this.observer.observe(this.$refs.sentinel);
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
};
</script>

<style lang="scss">
.base-phone-list {
  &__items {
    margin: 0;
    padding: 0;
    overflow: hidden;
    border-radius: $border-radius-root;
    background: $color-white;
  }

  &__selection-bar {
    position: sticky;
    // Under the navigation bar (the page header scrolls away on phones).
    top: $menu-top-bar-height;
    @include top-bar-follower;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding: 8px 12px;
    border-radius: $border-radius-root;
    background: $color-white;
    box-shadow: $shadow-dropdown;
  }

  &__selection-count {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: $color-normal;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-height: 64px;
    // Room for the floating "next tender" and pagination controls of the other pages.
    padding: 16px 0 96px;

    &.is-tight {
      min-height: 0;
      padding: 16px 0 0;

      &:empty {
        padding: 0;
      }
    }
  }

  &__footer-text {
    margin: 0;
    font-size: 13px;
    color: $color-slate-500;
  }

  &__more {
    min-height: 44px;
    padding: 0 20px;
    border: 1px solid $color-border;
    border-radius: $radius-full;
    background: $color-white;
    font-size: 14px;
    font-weight: 500;
    color: $color-accent;
  }
}
</style>
